import { t } from "../../i18n";
import type { ReactNode } from "react";
import type { ReportSectionId } from "@tasduvar/engine";
import type { ReportModel, ReportWall } from "./model";
import { Cover } from "./sections/Cover";
import { coverText } from "./cover";
import { ProjectInfo } from "./sections/ProjectInfo";
import { Inputs } from "./sections/Inputs";
import { Drawing, ForceDiagram } from "./sections/Drawing";
import { SeismicParams } from "./sections/SeismicParams";
import { Coefficients } from "./sections/Coefficients";
import { Forces } from "./sections/Forces";
import { Steps } from "./sections/Steps";
import { StabilityTbdy, StabilityClassic, BasePressureSection } from "./sections/Stability";
import { BearingEc8, ec8Cases } from "./sections/BearingEc8";
import { Joints } from "./sections/Joints";
import { RcDesign, ReinforcementSection } from "./sections/RcDesign";
import { GlobalStabilitySection } from "./sections/GlobalStability";
import { QuantitiesSection, CostSection } from "./sections/Quantities";
import { Warnings } from "./sections/Warnings";
import { Summary } from "./sections/Summary";
import { wallAdvisories } from "./advisories";
import { References } from "./sections/References";
import "./report.css";

interface Page { id: ReportSectionId; wallName?: string; body: ReactNode }

/** Duvar düzeyindeki bölümler; her duvar için sırayla tekrarlanır. */
function wallPages(rw: ReportWall, m: ReportModel): Page[] {
  const s = m.settings.sections, detailed = m.settings.preset === "detayli" || !!s.steps;
  const { wall, result, error } = rw;
  const pages: Page[] = [];
  if (s.inputs) pages.push({ id: "inputs", wallName: wall.name, body: <Inputs wall={wall} /> });
  if (!result) {
    pages.push({ id: "warnings", wallName: wall.name, body: <div data-block="error"><h2>{t("Hesap yapılamadı")}</h2><p className="bad">{error}</p></div> });
    return pages;
  }
  if (s.drawing) pages.push({ id: "drawing", wallName: wall.name, body: <Drawing wall={wall} result={result} /> });
  if (s.forceDiagram) pages.push({ id: "forceDiagram", wallName: wall.name, body: <ForceDiagram wall={wall} result={result} /> });
  if (s.seismicParams) pages.push({ id: "seismicParams", wallName: wall.name, body: <SeismicParams result={result} /> });
  if (s.coefficients) pages.push({ id: "coefficients", wallName: wall.name, body: <Coefficients result={result} detailed={detailed} /> });
  if (s.forces) pages.push({ id: "forces", wallName: wall.name, body: <Forces result={result} /> });
  if (s.steps) pages.push({ id: "steps", wallName: wall.name, body: <Steps result={result} /> });
  if (s.stabilityTbdy) pages.push({ id: "stabilityTbdy", wallName: wall.name, body: <StabilityTbdy result={result} /> });
  if (s.stabilityClassic) pages.push({ id: "stabilityClassic", wallName: wall.name, body: <StabilityClassic result={result} /> });
  if (s.basePressure) pages.push({ id: "basePressure", wallName: wall.name, body: <BasePressureSection result={result} detailed={detailed} /> });
  if (s.bearingEc8 && ec8Cases(result).length) pages.push({ id: "bearingEc8", wallName: wall.name, body: <BearingEc8 result={result} /> });
  if (s.joints && !result.rc) pages.push({ id: "joints", wallName: wall.name, body: <Joints result={result} /> });
  if (s.rcDesign && result.rc) pages.push({ id: "rcDesign", wallName: wall.name, body: <RcDesign result={result} /> });
  if (s.reinforcement && result.rc) pages.push({ id: "reinforcement", wallName: wall.name, body: <ReinforcementSection wall={wall} result={result} /> });
  if (s.globalStability) pages.push({ id: "globalStability", wallName: wall.name, body: <GlobalStabilitySection rw={rw} wall={wall} result={result} /> });
  if (s.quantities && rw.quantities) pages.push({ id: "quantities", wallName: wall.name, body: <QuantitiesSection wall={wall} q={rw.quantities} /> });
  if (s.warnings) pages.push({ id: "warnings", wallName: wall.name, body: <Warnings wall={wall} result={result} advisories={wallAdvisories(wall, m.project)} /> });
  return pages;
}

export function reportPages(m: ReportModel): Page[] {
  const s = m.settings.sections;
  const pages: Page[] = [];
  if (s.cover) pages.push({ id: "cover", body: <Cover m={m} /> });
  if (s.projectInfo) pages.push({ id: "projectInfo", body: <ProjectInfo m={m} /> });
  for (const rw of m.walls) pages.push(...wallPages(rw, m));
  if (s.quantities && m.walls.some((w) => w.quantities)) pages.push({ id: "quantities", body: <CostSection m={m} /> });
  if (s.summary) pages.push({ id: "summary", body: <Summary m={m} /> });
  if (s.references) pages.push({ id: "references", body: <References designCode={m.walls.find((w) => w.result)?.result?.designCode ?? "tbdy2018"} rc={m.walls.map((w) => w.result?.rc).find((x) => x)} /> });
  return pages;
}

export function Report({ model }: { model: ReportModel }): JSX.Element {
  const pages = reportPages(model);
  const date = model.generatedAt.slice(0, 10);
  const footerExtra = coverText(model).footerExtra;
  return (
    <div className="report" data-digest={model.digest}>
      {pages.map((p, i) => (
        <section key={`${p.id}-${p.wallName ?? ""}-${i}`} className="report-page" data-section={p.id} data-wall={p.wallName}>
          {model.branding.watermark ? <div className="watermark" aria-hidden><span>{model.branding.watermark}</span></div> : null}
          <div className="content">
            <div className="report-header"><span>{model.project.info.name}{p.wallName ? ` · ${p.wallName}` : ""}</span><span>{date} · {model.project.info.revision}</span></div>
            {p.body}
          </div>
          <div className="report-footer"><span>{t("Hesap motoru v")}{model.engineVersion} {t("· dosya özeti")} {model.digest} · {model.branding.footerText}{footerExtra ? ` · ${footerExtra}` : ""}</span><span>{t("Sayfa")} {i + 1}/{pages.length}</span></div>
        </section>
      ))}
    </div>
  );
}
